<template>
  <aside class="my-12 rounded-2xl border border-line bg-surface p-6">
    <div class="flex flex-col gap-6 sm:flex-row">
      <img
        v-if="photo"
        :src="photo"
        :alt="name"
        class="h-20 w-20 flex-shrink-0 rounded-full object-cover"
      />
      <div>
        <h3 class="text-lg font-semibold text-ink">
          About {{ name }}
        </h3>
        <p class="mb-2 text-sm text-ink-4">{{ jobTitle }}, {{ organization }}</p>
        <p class="text-[15px] leading-relaxed text-ink-2">
          <slot>{{ bio }}</slot>
        </p>
        <div v-if="sameAs?.length" class="mt-3 flex flex-wrap gap-3 text-sm">
          <a
            v-for="link in sameAs"
            :key="link.url"
            :href="link.url"
            target="_blank"
            rel="noopener me"
            class="font-medium text-accent hover:text-accent-hover"
          >{{ link.label }}</a>
        </div>
      </div>
    </div>
    <JsonLd :data="schema" />
  </aside>
</template>


<script setup lang="ts">
import { computed } from 'vue'
import JsonLd from './JsonLd.vue'
import { personLd } from '@/lib/seoEntities'

export interface SocialLink {
  label: string
  url: string
}

const props = defineProps<{
  name: string
  jobTitle: string
  organization: string
  organizationUrl?: string
  photo?: string
  bio?: string
  sameAs?: SocialLink[]
}>()

// Same Person node (and @id) as the Article author, with absolute image URL and the
// founder's profiles as sameAs when the page doesn't list its own.
const schema = computed(() =>
  personLd({
    name: props.name,
    jobTitle: props.jobTitle,
    ...(props.photo ? { photo: props.photo } : {}),
    ...(props.sameAs?.length ? { sameAs: props.sameAs } : {}),
    withContext: true,
  }),
)
</script>
